import instance from './instance';
import { ENV } from '@/constants/env';
import {
  BossBabyToolDto,
  CreateBossBabyToolDto,
  UpdateBossBabyToolDto,
  BossBabyTaskDto,
  CreateBossBabyTaskDto,
  UpdateBossBabyTaskDto,
  BossBabySearchParams,
  BossBabyCompanySettingDto,
  UpdateBossBabyCompanySettingDto,
  ResultListDto
} from '@/dto/BossBabyDto';

const PREFIX_PATH = `${ENV.PRE_FIX}/${ENV.ADMIN_PATH}/boss-baby`;

export const bossBabyApi = {
  // ========== Tool APIs ==========
  async getTools(params?: BossBabySearchParams): Promise<ResultListDto<BossBabyToolDto>> {
    const apiUrl = `${PREFIX_PATH}/tools`;
    console.log('[API_CALL]', { url: apiUrl, method: 'GET', params });

    const response = await instance.get(apiUrl, { params });
    const resData = response as any;

    if (!resData.success) {
      throw new Error(resData.message || 'Failed to fetch tools');
    }

    return resData.data;
  },

  async getTool(toolId: number): Promise<BossBabyToolDto> {
    const apiUrl = `${PREFIX_PATH}/tools/${toolId}`;
    console.log('[API_CALL]', { url: apiUrl, method: 'GET' });

    const response = await instance.get(apiUrl);
    const resData = response as any;

    if (!resData.success) {
      throw new Error(resData.message || 'Failed to fetch tool');
    }

    return resData.data;
  },

  async createTool(data: CreateBossBabyToolDto): Promise<BossBabyToolDto> {
    const apiUrl = `${PREFIX_PATH}/tools`;
    console.log('[API_CALL]', { url: apiUrl, method: 'POST', data });

    const response = await instance.post(apiUrl, data);
    const resData = response as any;

    if (!resData.success) {
      throw new Error(resData.message || 'Failed to create tool');
    }

    return resData.data;
  },

  async updateTool(
    toolId: number,
    data: UpdateBossBabyToolDto
  ): Promise<BossBabyToolDto> {
    const apiUrl = `${PREFIX_PATH}/tools/${toolId}`;
    console.log('[API_CALL]', { url: apiUrl, method: 'PUT', data });

    const response = await instance.put(apiUrl, data);
    const resData = response as any;

    if (!resData.success) {
      throw new Error(resData.message || 'Failed to update tool');
    }

    return resData.data;
  },

  async deleteTool(toolId: number): Promise<void> {
    const apiUrl = `${PREFIX_PATH}/tools/${toolId}`;
    console.log('[API_CALL]', { url: apiUrl, method: 'DELETE' });

    const response = await instance.delete(apiUrl);
    const resData = response as any;

    if (!resData.success) {
      throw new Error(resData.message || 'Failed to delete tool');
    }
  },

  // ========== Task APIs ==========
  async getTasks(params?: BossBabySearchParams): Promise<ResultListDto<BossBabyTaskDto>> {
    const apiUrl = `${PREFIX_PATH}/tasks`;
    console.log('[API_CALL]', { url: apiUrl, method: 'GET', params });

    const response = await instance.get(apiUrl, { params });
    const resData = response as any;

    if (!resData.success) {
      throw new Error(resData.message || 'Failed to fetch tasks');
    }

    return resData.data;
  },

  async getTask(taskId: number): Promise<BossBabyTaskDto> {
    const apiUrl = `${PREFIX_PATH}/tasks/${taskId}`;
    console.log('[API_CALL]', { url: apiUrl, method: 'GET' });

    const response = await instance.get(apiUrl);
    const resData = response as any;

    if (!resData.success) {
      throw new Error(resData.message || 'Failed to fetch task');
    }

    return resData.data;
  },

  async createTask(data: CreateBossBabyTaskDto): Promise<BossBabyTaskDto> {
    const apiUrl = `${PREFIX_PATH}/tasks`;
    console.log('[API_CALL]', { url: apiUrl, method: 'POST', data });

    const response = await instance.post(apiUrl, data);
    const resData = response as any;

    if (!resData.success) {
      throw new Error(resData.message || 'Failed to create task');
    }

    return resData.data;
  },

  async updateTask(
    taskId: number,
    data: UpdateBossBabyTaskDto
  ): Promise<BossBabyTaskDto> {
    const apiUrl = `${PREFIX_PATH}/tasks/${taskId}`;
    console.log('[API_CALL]', { url: apiUrl, method: 'PUT', data });

    const response = await instance.put(apiUrl, data);
    const resData = response as any;

    if (!resData.success) {
      throw new Error(resData.message || 'Failed to update task');
    }

    return resData.data;
  },

  async deleteTask(taskId: number): Promise<void> {
    const apiUrl = `${PREFIX_PATH}/tasks/${taskId}`;
    console.log('[API_CALL]', { url: apiUrl, method: 'DELETE' });

    const response = await instance.delete(apiUrl);
    const resData = response as any;

    if (!resData.success) {
      throw new Error(resData.message || 'Failed to delete task');
    }
  },

  // ========== Company Settings APIs ==========
  async getCompanySettings(comId: number): Promise<{
    tools: Array<BossBabyToolDto & { companySetting?: BossBabyCompanySettingDto }>;
    tasks: Array<BossBabyTaskDto & { companySetting?: BossBabyCompanySettingDto }>;
  }> {
    const apiUrl = `${PREFIX_PATH}/companies/${comId}/settings`;
    console.log('[API_CALL]', { url: apiUrl, method: 'GET' });

    const response = await instance.get(apiUrl);
    const resData = response as any;

    if (!resData.success) {
      throw new Error(resData.message || 'Failed to fetch company settings');
    }

    return resData.data;
  },

  async updateCompanyToolUsage(
    comId: number,
    toolId: number,
    data: UpdateBossBabyCompanySettingDto
  ): Promise<void> {
    const apiUrl = `${PREFIX_PATH}/companies/${comId}/tools/${toolId}`;
    console.log('[API_CALL]', { url: apiUrl, method: 'PUT', data });

    const response = await instance.put(apiUrl, data);
    const resData = response as any;

    if (!resData.success) {
      throw new Error(resData.message || 'Failed to update tool usage');
    }
  },

  async updateCompanyTaskUsage(
    comId: number,
    taskId: number,
    data: UpdateBossBabyCompanySettingDto
  ): Promise<void> {
    const apiUrl = `${PREFIX_PATH}/companies/${comId}/tasks/${taskId}`;
    console.log('[API_CALL]', { url: apiUrl, method: 'PUT', data });

    const response = await instance.put(apiUrl, data);
    const resData = response as any;

    if (!resData.success) {
      throw new Error(resData.message || 'Failed to update task usage');
    }
  },

  // ========== Utility APIs ==========
  async getAllActiveTools(): Promise<BossBabyToolDto[]> {
    const apiUrl = `${PREFIX_PATH}/tools`;
    const params = { isActive: true, limit: 100 };
    console.log('[API_CALL]', { url: apiUrl, method: 'GET', params });

    const response = await instance.get(apiUrl, { params });
    const resData = response as any;

    if (!resData.success) {
      throw new Error(resData.message || 'Failed to fetch active tools');
    }

    return resData.data.list;
  },

  async getToolsByCategory(category: string): Promise<BossBabyToolDto[]> {
    const apiUrl = `${PREFIX_PATH}/tools/category/${category}`;
    console.log('[API_CALL]', { url: apiUrl, method: 'GET' });

    const response = await instance.get(apiUrl);
    const resData = response as any;

    if (!resData.success) {
      throw new Error(resData.message || 'Failed to fetch tools by category');
    }

    return resData.data;
  },

  async getTasksByTool(toolName: string): Promise<BossBabyTaskDto[]> {
    const apiUrl = `${PREFIX_PATH}/tasks/by-tool/${toolName}`;
    console.log('[API_CALL]', { url: apiUrl, method: 'GET' });

    const response = await instance.get(apiUrl);
    const resData = response as any;

    if (!resData.success) {
      throw new Error(resData.message || 'Failed to fetch tasks by tool');
    }

    return resData.data;
  }
};